import { TagGroupDto } from "@closerplatform/spinner-openapi";
import { flow, Instance, types } from "mobx-state-tree";
import { logger } from "@fidget/common/utils";
import { servicesGetters } from "../utils";
import { TagGroupsStoreEventListener } from "./eventHandler";
import { notEmpty } from "@fidget/common/utils/not-empty";
import { ITag } from "../tags/TagsStore";

export const TagGroup = types.model("TagGroup", {
  id: types.identifier,
  name: types.string,
  enabled: types.boolean,
  showInConversationDetails: types.boolean,
  tagIds: types.array(types.string)
});

export const TagGroupsStore = types
  .model("TagGroupsStore", {
    tagGroups: types.map(TagGroup)
  })
  .views(servicesGetters)
  .views((self) => {
    const views = {
      get groups(): ITagGroup[] {
        return [...self.tagGroups.values()];
      },

      get enabledGroups(): ITagGroup[] {
        return views.groups.filter((tg) => tg.enabled);
      },

      getById(id: string): ITagGroup | undefined {
        return self.tagGroups.get(id);
      },

      getByName(name: string): ITagGroup | undefined {
        return Array.from(self.tagGroups.values()).find((el) => el.name === name);
      },

      getFromIds(ids: string[]): ITagGroup[] {
        return ids.map((id) => self.tagGroups.get(id)).filter(notEmpty);
      },

      getWithoutIds(ids: string[]): ITagGroup[] {
        return views.groups.filter((tg) => !ids.includes(tg.id));
      },

      getByTags(tags: ITag[]): ITagGroup[] {
        const tagsIds: string[] = tags.map((tag) => tag.id);
        return views.groups.filter((tagGroup) =>
          tagGroup.tagIds.some((tag) => tagsIds.indexOf(tag) >= 0)
        );
      }
    };

    return views;
  })
  .actions((self) => {
    const actions = {
      setTagGroups(tagGroups: TagGroupDto[]) {
        self.tagGroups.clear();
        tagGroups
          .map((group) =>
            TagGroup.create({
              id: group.id,
              enabled: group.active,
              name: group.name,
              showInConversationDetails: group.showInConversationDetails,
              tagIds: group.tags
            })
          )
          .forEach((g) => self.tagGroups.put(g));
      },

      // addTag(tag: TagDto) {
      //   self.orgTagsMap.set(tag.id, Tag.create({ ...tag }));
      // },

      // updateTag(tag: TagDto) {
      //   self.orgTagsMap.set(tag.id, { ...tag });
      // },

      // deleteTag(id: string) {
      //   self.orgTagsMap.delete(id);
      // },

      loadTagGroups: flow(function* () {
        try {
          const tagGroups: TagGroupDto[] = yield self.spinnerService.getTagGroups();
          actions.setTagGroups(tagGroups);
        } catch (e) {
          logger.error({ message: "Couldn't load org tag groups", error: e });
        }
      })
    };

    return actions;
  })
  .actions((self) => {
    const eventListenerName = "TagGroupsStoreEventListener";
    const actions = {
      afterCreate() {
        self.eventListener.addGlobalListener(
          new TagGroupsStoreEventListener(() => self as ITagGroupsStore, eventListenerName)
        );
      },

      beforeDestroy() {
        self.eventListener.removeGlobalListener(eventListenerName);
      }
    };

    return actions;
  });

export interface ITagGroup extends Instance<typeof TagGroup> { }
export interface ITagGroupsStore extends Instance<typeof TagGroupsStore> { }
